import {
  Button,
  ErrorMessage,
  Field,
  Input,
  Label,
  SelectInput,
} from "@web-app/components";
import { CODE_FORMAT_OPTIONS } from "@web-app/utils/scannableCode";

import {
  ScannableCodeEntry,
  emptyScannableCodeEntry,
} from "./scannableCodeEntries";

const formatOptions = [
  { label: "Workspace default", value: "" },
  ...CODE_FORMAT_OPTIONS,
];

const rowGrid =
  "grid grid-cols-[minmax(0,1fr)_4.5rem_9rem_auto_auto] items-center gap-2";

/** Barcode rows (code, quantity, format, required) for one stop of an order form. */
export function ScannableCodeEntriesEditor({
  entries,
  error,
  label = "Barcode Details",
  onChange,
}: {
  entries: ScannableCodeEntry[];
  error?: string;
  label?: string;
  onChange: (entries: ScannableCodeEntry[]) => void;
}) {
  const updateEntry = (index: number, patch: Partial<ScannableCodeEntry>) => {
    const next = [...entries];
    next[index] = { ...next[index], ...patch };
    onChange(next);
  };

  return (
    <Field>
      <Label>{label}</Label>
      <div className="space-y-2">
        {entries.length > 0 && (
          <div className={`${rowGrid} text-xs text-gray-500`}>
            <span>Code</span>
            <span>Qty</span>
            <span>Format</span>
            <span>Required</span>
            <span className="w-8" />
          </div>
        )}
        {entries.map((entry, index) => (
          <div key={index} className={rowGrid} data-testid="barcode-entry">
            <Input
              type="text"
              aria-label={`Barcode ${index + 1} code`}
              placeholder="e.g. 1727172171239"
              value={entry.code}
              invalid={!!error && !entry.code.trim()}
              onChange={(value) => updateEntry(index, { code: value })}
            />
            <Input
              type="number"
              aria-label={`Barcode ${index + 1} quantity`}
              min={1}
              step={1}
              value={entry.quantity}
              onChange={(value) => updateEntry(index, { quantity: value })}
            />
            <SelectInput
              size="xs"
              options={formatOptions}
              selected={entry.format}
              onChange={(value) =>
                updateEntry(index, {
                  format: value as ScannableCodeEntry["format"],
                })
              }
            />
            <span className="flex justify-center">
              <Input
                type="checkbox"
                aria-label={`Barcode ${index + 1} required`}
                value={entry.isRequired.toString()}
                onChange={(value) =>
                  updateEntry(index, { isRequired: value === "true" })
                }
              />
            </span>
            <Button
              plain
              icon="fasTrash"
              aria-label={`Remove barcode ${index + 1}`}
              onClick={() => onChange(entries.filter((_, i) => i !== index))}
            />
          </div>
        ))}
        <Button
          plain
          size="sm"
          onClick={() => onChange([...entries, emptyScannableCodeEntry()])}
        >
          + Add Barcode
        </Button>
        {error && <ErrorMessage message={error} />}
      </div>
    </Field>
  );
}
